export type SearchMode = "and" | "or";

export interface SearchFields {
  id?: string | null;
  barcode?: string | null;
  sku?: string | null;
  name?: string | null;
  text?: readonly (string | null | undefined)[];
}

export interface SearchOptions {
  mode?: SearchMode;
}

export interface SearchResult<T> {
  item: T;
  score: number;
}

/** Lowercase, strip Vietnamese marks, map đ to d, and collapse whitespace. */
export function normalizeSearchText(value: string | null | undefined): string {
  return (value ?? "")
    .normalize("NFD")
    .replace(/[\u0300-\u036f]+/g, "")
    .toLowerCase()
    .replace(/đ/g, "d")
    .replace(/\s+/g, " ")
    .trim();
}

export function tokenizeSearchQuery(query: string): string[] {
  const normalized = normalizeSearchText(query);
  return normalized ? normalized.split(" ") : [];
}

/** Levenshtein distance. Inputs should already be normalized when used for search. */
export function editDistance(left: string, right: string): number {
  const previous = Array.from({ length: right.length + 1 }, (_, index) => index);

  for (let row = 1; row <= left.length; row += 1) {
    let diagonal = previous[0];
    previous[0] = row;

    for (let column = 1; column <= right.length; column += 1) {
      const above = previous[column];
      previous[column] = left[row - 1] === right[column - 1]
        ? diagonal
        : 1 + Math.min(diagonal, above, previous[column - 1]);
      diagonal = above;
    }
  }

  return previous[right.length];
}

/** Returns the best score, or -1 when no field matches. */
export function scoreSearchFields(
  fields: SearchFields,
  query: string,
  options: SearchOptions = {},
): number {
  const tokens = tokenizeSearchQuery(query);
  if (tokens.length === 0) return 0;

  const mode = options.mode === "or" ? "or" : "and";
  let score = 0;
  let matched = 0;

  for (const token of tokens) {
    const tokenScore = scoreToken(fields, token);
    if (tokenScore < 0) {
      if (mode === "and") return -1;
      continue;
    }
    score += tokenScore;
    matched += 1;
  }

  return matched > 0 ? score : -1;
}

export function matchesSearchFields(
  fields: SearchFields,
  query: string,
  options: SearchOptions = {},
): boolean {
  return scoreSearchFields(fields, query, options) >= 0;
}

export function searchItems<T>(
  items: readonly T[],
  query: string,
  getFields: (item: T) => SearchFields,
  options: SearchOptions = {},
): SearchResult<T>[] {
  return items
    .map((item, index) => ({
      item,
      index,
      score: scoreSearchFields(getFields(item), query, options),
    }))
    .filter((result) => result.score >= 0)
    .sort((left, right) => right.score - left.score || left.index - right.index)
    .map(({ item, score }) => ({ item, score }));
}

/**
 * Union of `searchItems` over several queries (each query keeps AND inside),
 * ordered by best score. Equal scores keep the input order (stable).
 * A name equal to the whole line gets +1000 so "MK bánh" beats "MK bánh bao".
 * ponytail: rank-local bonus; if single-line ordering also needs it, move the
 * bonus into scoreSearchFields instead of widening this helper.
 */
export function rankSearchResults<T>(
  items: readonly T[],
  queries: readonly string[],
  getFields: (item: T) => SearchFields,
  options: SearchOptions = {},
): SearchResult<T>[] {
  const order = new Map(items.map((item, index) => [item, index] as const));
  const best = new Map<T, { item: T; score: number; index: number }>();
  for (const query of queries) {
    const normalizedQuery = normalizeSearchText(query);
    for (const result of searchItems(items, query, getFields, options)) {
      const exactName = normalizeSearchText(getFields(result.item).name) === normalizedQuery ? 1_000 : 0;
      const score = result.score + exactName;
      const current = best.get(result.item);
      if (!current) best.set(result.item, { item: result.item, score, index: order.get(result.item) ?? 0 });
      else if (score > current.score) current.score = score;
    }
  }
  return [...best.values()]
    .sort((left, right) => right.score - left.score || left.index - right.index)
    .map(({ item, score }) => ({ item, score }));
}

function scoreToken(fields: SearchFields, token: string): number {
  const identifierScores = [
    scoreIdentifier(fields.id, token, 9_000, 5_000, 3_000),
    scoreIdentifier(fields.barcode, token, 8_000, 4_500, 2_800),
    scoreIdentifier(fields.sku, token, 7_000, 4_000, 2_600),
  ];

  let best = Math.max(...identifierScores);
  const textValues = [fields.name, ...(fields.text ?? [])];
  textValues.forEach((value, index) => {
    const textScore = scoreText(value, token, index === 0 ? 2_000 : 1_800);
    best = Math.max(best, textScore);
  });
  return best;
}

function scoreIdentifier(
  value: string | null | undefined,
  token: string,
  exactScore: number,
  prefixScore: number,
  containsScore: number,
): number {
  const normalized = normalizeSearchText(value);
  if (!normalized) return -1;
  if (normalized === token) return exactScore;
  if (normalized.startsWith(token)) return prefixScore;
  if (normalized.includes(token)) return containsScore;
  return -1;
}

function scoreText(
  value: string | null | undefined,
  token: string,
  exactScore: number,
): number {
  const normalized = normalizeSearchText(value);
  if (!normalized) return -1;

  const words = normalized.split(" ");
  if (normalized === token || words.includes(token)) return exactScore;
  if (normalized.startsWith(token)) return exactScore - 150;
  if (words.some((word) => word.startsWith(token))) return exactScore - 250;
  if (normalized.includes(token)) return exactScore - 400;

  const maxDistance = fuzzyDistanceLimit(token);
  if (maxDistance === 0 || !/^[\p{L}]+$/u.test(token)) return -1;
  if (words.some((word) => editDistance(word, token) <= maxDistance)) {
    return exactScore - 650 - maxDistance;
  }
  return -1;
}

function fuzzyDistanceLimit(token: string): number {
  if (token.length <= 3) return 0;
  return token.length <= 7 ? 1 : 2;
}
